import { useCallback, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Swal from "sweetalert2";
import { userRequest } from "../../requestMethods";

// Seção "Instagram" da configuração da empresa:
// - mostra o status do vínculo (conta @username conectada ou não)
// - "Conectar" abre o OAuth do Instagram num POPUP; ao terminar, o callback
//   avisa esta aba via postMessage e o popup fecha sozinho (você fica aqui).
// - fallback: se abrir na mesma aba, lê ?instagram=... da URL de retorno.
// - "Desvincular" remove o vínculo
export default function InstagramConnect({ companyId, isAdmin }) {
  const [status, setStatus] = useState({ connected: false });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!companyId) return;
    try {
      const res = await userRequest.get(`${companyId}/instagram/status`);
      setStatus(res.data || { connected: false });
    } catch (e) {
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Exibe o resultado da conexão (usado pelo popup e pelo fallback de URL).
  const showResult = useCallback(
    (result, msg) => {
      if (result === "success") {
        Swal.fire("Instagram conectado", msg, "success");
      } else if (result === "notbusiness") {
        Swal.fire({
          icon: "warning",
          title: "Conta não é Profissional",
          text:
            msg ||
            "Converta seu Instagram para conta Profissional (Comercial/Criador) e tente novamente.",
        });
      } else {
        Swal.fire("Não foi possível conectar", msg, "error");
      }
      fetchStatus();
    },
    [fetchStatus],
  );

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Mensagem vinda do popup do callback (postMessage).
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.source !== "instagram-connect") return;
      setConnecting(false);
      showResult(data.status, data.message || "");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [showResult]);

  // Retorno do callback OAuth: ?instagram=success|error|notbusiness&msg=...
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("instagram");
    if (!result) return;
    const msg = params.get("msg") || "";
    showResult(result, msg);
    // limpa a query da URL sem recarregar
    params.delete("instagram");
    params.delete("msg");
    const clean =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : "");
    window.history.replaceState({}, "", clean);
  }, [showResult]);

  const handleConnect = async () => {
    // Abre o popup JÁ no clique (síncrono) pra não ser bloqueado; a URL é
    // definida depois que o backend responde.
    const popup = window.open(
      "",
      "ig_oauth",
      "width=600,height=760,menubar=no,toolbar=no",
    );
    try {
      setConnecting(true);
      const res = await userRequest.get(`${companyId}/instagram/oauth/url`);
      if (res.data && res.data.url) {
        if (popup && !popup.closed) {
          popup.location.href = res.data.url;
          // se o usuário fechar o popup sem concluir, reabilita o botão e
          // reconsulta o status (caso a conexão tenha dado certo mesmo assim)
          const timer = setInterval(() => {
            if (popup.closed) {
              clearInterval(timer);
              setConnecting(false);
              fetchStatus();
            }
          }, 800);
        } else {
          // popup bloqueado → cai pro fluxo de mesma aba
          window.location.href = res.data.url;
        }
      } else {
        throw new Error("URL de conexão indisponível.");
      }
    } catch (e) {
      setConnecting(false);
      if (popup && !popup.closed) popup.close();
      const status = e.response && e.response.status;
      const serverData = e.response && e.response.data;
      const serverMsg =
        (serverData && (serverData.message || serverData)) || e.message;
      const url =
        (e.config && `${e.config.baseURL || ""}${e.config.url || ""}`) || "";
      Swal.fire(
        "Erro ao conectar",
        `HTTP ${status || "sem resposta"} — ${
          typeof serverMsg === "string" ? serverMsg : JSON.stringify(serverMsg)
        }\n\nURL chamada: ${url}`,
        "error",
      );
    }
  };

  const handleDisconnect = async () => {
    const ok = await Swal.fire({
      icon: "question",
      title: "Desvincular Instagram?",
      text: "Você precisará conectar novamente para publicar.",
      showCancelButton: true,
      confirmButtonText: "Desvincular",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed) return;
    try {
      await userRequest.delete(`${companyId}/instagram/disconnect`);
      setStatus({ connected: false });
      Swal.fire("Pronto", "Conta desvinculada.", "success");
    } catch (e) {
      Swal.fire("Erro", "Não foi possível desvincular.", "error");
    }
  };

  return (
    <div className="cust-card">
      <div className="cust-section-header">Instagram</div>
      <div style={{ padding: "16px" }}>
        {loading ? (
          <span>Carregando…</span>
        ) : status.connected ? (
          <div
            style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}
          >
            <span>
              Conta conectada:{" "}
              <strong>@{status.username}</strong>{" "}
              <span style={{ color: "#3a833a" }}>● Profissional</span>
            </span>
            {isAdmin && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDisconnect}
              >
                Desvincular
              </Button>
            )}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <span
              style={{
                color: "#888",
                fontSize: "13px",
                lineHeight: 1.5,
              }}
            >
              Nenhuma conta vinculada. Conecte uma conta{" "}
              <strong>Instagram Profissional (Comercial ou de Criador de
              conteúdo)</strong>{" "}
              para publicar produtos direto do painel.
            </span>
            {isAdmin && (
              <div>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  disabled={connecting}
                  onClick={handleConnect}
                >
                  {connecting ? "Abrindo…" : "Conectar Instagram"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
